import { useEffect, useMemo } from 'preact/hooks';
import useAppDispatch from '../../../hooks/useAppDispatch';
import useAppSelector from '../../../hooks/useAppSelector';
import type { ChatSidebarFeatureItem } from '../../../models/features/sidebar/ChatSidebarFeatureItem';
import Chat from '../../chat/Chat';
import './ChatSidebarFeature.less';
import { useTranslation } from '../../../hooks/useTranslation';
import { dispatchEvent } from '../../../services/EventService';
import { useSeedRandom } from '../../../hooks/useSeedRandom';
import ProductRelatedQuestionList from '../../product/ProductRelatedQuestionList';
import ChatClickableButton from '../../chat/ChatClickableButton';
import AssistantChatMessage from '../../chat/AssistantChatMessage';

const ChatSidebarFeature = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const sidebarFeatureItem = useAppSelector(
        state => state.featureState.sidebarFeatureItem as ChatSidebarFeatureItem
    );
    const mode = useAppSelector(state => state.uiState.mode);
    const comparingProducts = useAppSelector(state =>
        state.featureState.mainFeatureItem?.featureType === 'product-comparison'
            ? state.featureState.mainFeatureItem.comparingProducts
            : undefined
    );
    const detailedProduct = useAppSelector(state =>
        state.featureState.mainFeatureItem?.featureType === 'product-detail'
            ? state.featureState.mainFeatureItem.product
            : undefined
    );

    const allRelatedQuestions = useMemo(
        () =>
            shuffle(
                comparingProducts?.flatMap(
                    product => product.relatedComparisonQuestions || []
                ) ||
                    detailedProduct?.relatedAnalysisQuestions ||
                    []
            ),
        [comparingProducts, detailedProduct]
    );
    const random = useSeedRandom(
        sidebarFeatureItem.featureUuid,
        allRelatedQuestions.length
    );
    const selectedQuestions = [
        ...allRelatedQuestions,
        ...allRelatedQuestions
    ].slice(random, random + Math.min(3, allRelatedQuestions.length));

    useEffect(() => {
        const timeout = setTimeout(() => {
            const childToShow = document.querySelector<HTMLElement>(
                `.bri-Chat-messages > *:${
                    sidebarFeatureItem.chatMessages.length > 1
                        ? 'last-child'
                        : 'first-child'
                }`
            );
            childToShow?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
        return () => clearTimeout(timeout);
    }, [
        sidebarFeatureItem.chatMessages,
        sidebarFeatureItem.loadingStatus,
        mode
    ]);
    useEffect(() => {
        if (sidebarFeatureItem.loadingStatus !== 'loading') {
            const textarea = document.querySelector<HTMLTextAreaElement>(
                '.bri-ChatInput textarea'
            );
            textarea?.focus();
        }
    }, [sidebarFeatureItem.loadingStatus]);
    return (
        <div className={`bri-ChatSidebarFeature `}>
            <Chat
                status={sidebarFeatureItem.loadingStatus}
                onSubmit={sentence => {
                    dispatchEvent({
                        type: 'bri.input_chat',
                        isInteraction: true,
                        chatType: sidebarFeatureItem.chatType,
                        inputValue: sentence
                    });
                    dispatch.featureState.appendNewChatMessage({
                        role: 'user',
                        content: {
                            contentType: 'plain-text',
                            text: sentence
                        }
                    });
                }}
                inputPlaceholder={
                    sidebarFeatureItem.chatType === 'product-detail' ||
                    sidebarFeatureItem.chatType === 'product-comparison'
                        ? t('PRODUCT_ANALYSIS_QUESTION_INPUT_PLACEHOLDER')
                        : t('PRODUCT_SEARCH_CHAT_INPUT_PLACEHOLDER')
                }
                chatMessages={sidebarFeatureItem.chatMessages}
            >
                {sidebarFeatureItem.chatType === 'product-detail' && (
                    <AssistantChatMessage
                        assistantChatMessage={{
                            timestamp: 0,
                            uuid: '',
                            role: 'assistant',
                            content: {
                                contentType: 'markdown',
                                markdown: t(
                                    'PRODUCT_ANALYSIS_QUESTION_INITIAL_MESSAGE'
                                )
                            }
                        }}
                    />
                )}
                {sidebarFeatureItem.chatType === 'product-comparison' && (
                    <AssistantChatMessage
                        assistantChatMessage={{
                            timestamp: 0,
                            uuid: '',
                            role: 'assistant',
                            content: {
                                contentType: 'markdown',
                                markdown: t(
                                    'PRODUCT_COMPARISON_QUESTION_INITIAL_MESSAGE'
                                )
                            }
                        }}
                    />
                )}
                {sidebarFeatureItem.chatMessages.length === 0 &&
                    selectedQuestions.length > 0 && (
                        <ProductRelatedQuestionList>
                            {selectedQuestions.map((question, index) => (
                                <ChatClickableButton
                                    animationDelay={400 + index * 200}
                                    onClick={() => {
                                        if (
                                            comparingProducts &&
                                            comparingProducts?.length > 0
                                        ) {
                                            dispatchEvent({
                                                type: 'bri.click_suggested_question',
                                                isInteraction: true,
                                                productIds:
                                                    comparingProducts.map(
                                                        product =>
                                                            product.productId
                                                    ),
                                                source: 'brilliant',
                                                brilliantSource: 'ui'
                                            });
                                        } else if (detailedProduct) {
                                            dispatchEvent({
                                                type: 'bri.click_suggested_question',
                                                isInteraction: true,
                                                productIds: [
                                                    detailedProduct.productId
                                                ],
                                                source: 'brilliant',
                                                brilliantSource: 'ui'
                                            });
                                        }
                                        dispatch.featureState.appendNewChatMessage(
                                            {
                                                role: 'user',
                                                content: {
                                                    contentType: 'plain-text',
                                                    text: question
                                                }
                                            }
                                        );
                                    }}
                                >
                                    {question}
                                </ChatClickableButton>
                            ))}
                        </ProductRelatedQuestionList>
                    )}
            </Chat>
        </div>
    );
};

export default ChatSidebarFeature;

function shuffle(array: string[]): string[] {
    // From https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
    let currentIndex = array.length;
    while (currentIndex != 0) {
        const randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex],
            array[currentIndex]
        ];
    }
    return array;
}
